import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getJobRows } from "../lib/api";
import { getUser } from "../lib/auth";
import Sidebar from "../components/Sidebar";

const PAGE_SIZE = 100;

const COLUMNS = [
  { key: "system_name",          label: "System Name"           },
  { key: "vendor_customer_num",  label: "Vendor / Customer #"   },
  { key: "vendor_customer_name", label: "Vendor / Customer Name"},
  { key: "name_line_2",          label: "Name Line 2"           },
  { key: "address_seq",          label: "Addr Seq"              },
  { key: "address_line_1",       label: "Address Line 1"        },
  { key: "address_line_2",       label: "Address Line 2"        },
  { key: "address_line_3",       label: "Address Line 3"        },
  { key: "address_line_4",       label: "Address Line 4"        },
  { key: "po_box",               label: "PO Box"                },
  { key: "city",                 label: "City"                  },
  { key: "state_province",       label: "State / Province"      },
  { key: "zip_postal_code",      label: "ZIP / Postal Code"     },
  { key: "country",              label: "Country"               },
  { key: "phone",                label: "Phone"                 },
  { key: "email",                label: "Email"                 },
  { key: "tax_id",               label: "Tax ID"                },
  { key: "website_domain",       label: "Website Domain"        },
];

export default function ViewJobData() {
  const { jobId }  = useParams();
  const navigate   = useNavigate();
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [filename, setFilename] = useState("");
  const [rows,     setRows]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [search,   setSearch]   = useState("");
  const [inputVal, setInputVal] = useState("");
  const debounceRef = useRef(null);

  const fetchRows = useCallback(async (pg, q) => {
    setLoading(true);
    try {
      const res = await getJobRows(jobId, { page: pg, limit: PAGE_SIZE, q });
      setRows(res.rows || []);
      setTotal(res.total || 0);
      setFilename(res.filename || "");
      setError("");
    } catch (e) {
      setError(e.message || "Could not load data.");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchRows(1, "");
  }, [fetchRows]);

  function handleSearchInput(e) {
    const val = e.target.value;
    setInputVal(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
      fetchRows(1, val);
    }, 350);
  }

  function goToPage(pg) {
    setPage(pg);
    fetchRows(pg, search);
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div style={styles.page}>
      <div style={styles.topStripe} />
      <div style={styles.body}>
      <Sidebar active="dashboard" />

      {/* Main */}
      <main style={styles.main}>
        <div style={styles.header}>
          <div>
            <Link to={`/process/${jobId}`} style={styles.backLink}>← Back to job</Link>
            <h1 style={styles.pageTitle}>Uploaded data</h1>
            <p style={styles.pageSub}>{filename && `${filename} · `}Job #{jobId}</p>
          </div>
        </div>

        {/* Error loading */}
        {error && !loading && (
          <div style={styles.errorCard}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>⚠</div>
            <p style={{ color: "#f87171", margin: 0 }}>{error}</p>
          </div>
        )}

        {!error && (
          <>
            {/* Toolbar */}
            <div style={styles.toolbar}>
              <div style={styles.countChip}>
                {loading ? "Loading…" : `${total.toLocaleString()} record${total !== 1 ? "s" : ""}${search ? " matched" : ""}`}
              </div>
              <input
                style={styles.searchInput}
                type="text"
                placeholder="Search by name, ID, city, email…"
                value={inputVal}
                onChange={handleSearchInput}
              />
            </div>

            {/* Table */}
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, ...styles.rowNumTh }}>#</th>
                    {COLUMNS.map(c => (
                      <th key={c.key} style={styles.th}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={COLUMNS.length + 1} style={styles.loadingCell}>
                        <div style={styles.spinner} />
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={COLUMNS.length + 1} style={styles.emptyCell}>
                        {search ? "No records match your search." : "No data rows found."}
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, ri) => (
                      <tr key={ri} style={styles.tr}>
                        <td style={{ ...styles.td, ...styles.rowNumTd }}>{row.row_number}</td>
                        {COLUMNS.map(c => (
                          <td key={c.key} style={styles.td}>
                            {row[c.key] ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button style={styles.pageBtn} disabled={page <= 1} onClick={() => goToPage(page - 1)}>← Prev</button>
                <span style={styles.pageInfo}>Page {page} of {totalPages} · rows {((page-1)*PAGE_SIZE)+1}–{Math.min(page*PAGE_SIZE, total)} of {total.toLocaleString()}</span>
                <button style={styles.pageBtn} disabled={page >= totalPages} onClick={() => goToPage(page + 1)}>Next →</button>
              </div>
            )}
          </>
        )}
      </main>
      </div>
    </div>
  );
}

const styles = {
  page:      { display: "flex", flexDirection: "column", minHeight: "100vh", background: "#f8f9fb", fontFamily: "'Inter', system-ui, sans-serif" },
  topStripe: { height: 4, background: "linear-gradient(90deg, #0176CE, #F37428)", flexShrink: 0 },
  body:      { display: "flex", flex: 1 },
  main:       { flex: 1, padding: "36px 40px", overflowY: "auto", minWidth: 0 },
  header:     { marginBottom: 24 },
  backLink:   { color: "#6b7280", fontSize: 13, textDecoration: "none", display: "block", marginBottom: 8 },
  pageTitle:  { color: "#111827", fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: "-0.5px" },
  pageSub:    { color: "#6b7280", fontSize: 14, margin: "4px 0 0" },
  errorCard:  { background: "#fff", border: "1px solid #fecaca", borderRadius: 12, padding: "40px 24px", textAlign: "center" },
  toolbar: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" },
  countChip: { background: "#fff", border: "1px solid #e2e6ef", borderRadius: 6, color: "#374151", fontSize: 13, fontWeight: 500, padding: "6px 12px", flexShrink: 0 },
  searchInput: { flex: 1, minWidth: 200, maxWidth: 400, background: "#fff", border: "1px solid #e2e6ef", borderRadius: 7, color: "#111827", fontSize: 13, outline: "none", padding: "7px 12px" },
  tableWrap: { background: "#fff", border: "1px solid #e2e6ef", borderRadius: 12, overflow: "auto" },
  table:  { width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 1400 },
  th: { background: "#f8f9fb", borderBottom: "1px solid #e2e6ef", color: "#6b7280", fontSize: 10, fontWeight: 600, letterSpacing: "0.05em", padding: "9px 12px", textAlign: "left", textTransform: "uppercase", whiteSpace: "nowrap" },
  rowNumTh: { color: "#d1d5db", width: 36, textAlign: "center" },
  tr: { borderBottom: "1px solid #f3f4f6" },
  td: { color: "#374151", padding: "9px 12px", whiteSpace: "nowrap", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" },
  rowNumTd: { color: "#d1d5db", fontSize: 11, textAlign: "center", padding: "9px 8px" },
  loadingCell: { padding: "48px 24px", textAlign: "center" },
  emptyCell: { color: "#9ca3af", fontSize: 14, padding: "40px 24px", textAlign: "center" },
  spinner: { width: 28, height: 28, border: "3px solid #e9ecf3", borderTop: "3px solid #6c63ff", borderRadius: "50%", animation: "spin 0.8s linear infinite", display: "inline-block" },
  pagination: { display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginTop: 20 },
  pageBtn: { background: "#fff", border: "1px solid #e2e6ef", borderRadius: 6, color: "#374151", cursor: "pointer", fontSize: 13, padding: "7px 16px" },
  pageInfo: { color: "#6b7280", fontSize: 13 },
};
